import type { ISubscriptionRequestRepository } from '../ISubscriptionRequestRepository';
import type {
  SubscriptionRequestCreateDTO,
  SubscriptionRequestDTO,
  SubscriptionRequestFilterDTO,
  SubscriptionRequestHistoryCreateDTO,
  SubscriptionRequestHistoryDeleteRq,
  SubscriptionRequestHistoryDTO,
  SubscriptionRequestHistoryUpdateDTO,
  SubscriptionRequestStatusUpdateRq,
  SubscriptionRequestUpdateDTO,
} from '../subscriptionRequest.types';
import { injectable } from 'inversify';
import qs from 'qs';
import API from '@repositories/Axios';
import type { Page } from '@type/Page';

@injectable()
export class SubscriptionRequestRepository implements ISubscriptionRequestRepository {
  async create(data: SubscriptionRequestCreateDTO): Promise<SubscriptionRequestDTO> {
    const formData = new FormData();

    const requestData = {
      subscriptionRequests: [
        {
          productPlanId: data.subscriptionRequests[0].productPlanId,
          softwareId: data.subscriptionRequests[0].softwareId,
          inquiry: data.subscriptionRequests[0].inquiry,
          startDate: data.subscriptionRequests[0].startDate,
          subscriptionType: data.subscriptionRequests[0].subscriptionType,
          timeZone: data.subscriptionRequests[0].timeZone,
          requester: data.subscriptionRequests[0].requester,
          files: data.subscriptionRequests[0].files,
        },
      ],
      tenantId: data.tenantId,
    };
    formData.append('request', JSON.stringify(requestData));

    if (data.subscriptionRequests[0].files && data.subscriptionRequests[0].files.length > 0) {
      data.subscriptionRequests[0].files.forEach((file: File) => {
        formData.append('files', file);
      });
    }

    const result = await API.post<SubscriptionRequestDTO>(`/api/v1/subscription-requests`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return result.data;
  }

  async getOne(id: number): Promise<SubscriptionRequestDTO> {
    const result = await API.get<SubscriptionRequestDTO>(`/api/v1/subscription-requests/${id}`);
    return result.data;
  }

  async getList(queries?: SubscriptionRequestFilterDTO): Promise<Page<SubscriptionRequestDTO>> {
    const queryString = qs.stringify(queries, { skipNulls: true });
    const result = await API.get<Page<SubscriptionRequestDTO>>(`/api/v1/subscription-requests?${queryString}`);
    return result.data;
  }

  async update(id: number, data: SubscriptionRequestUpdateDTO): Promise<SubscriptionRequestDTO> {
    const formData = new FormData();

    // request 객체를 JSON 문자열로 변환하여 추가
    const requestData = {
      productPlanId: data.productPlanId,
      managerId: data.managerId,
      status: data.status,
      inquiry: data.inquiry,
      startDate: data.startDate,
      endDate: data.endDate,
      timeZone: data.timeZone,
      requester: data.requester,
      files: data.files,
      existingFileIds: data.existingFileIds,
    };
    formData.append('request', JSON.stringify(requestData));

    if (data.files && data.files.length > 0) {
      data.files.forEach(file => {
        formData.append('files', file);
      });
    }

    const result = await API.put<SubscriptionRequestDTO>(`/api/v1/subscription-requests/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return result.data;
  }

  async delete(id: number): Promise<void> {
    await API.delete<SubscriptionRequestDTO>(`/api/v1/subscription-requests/${id}`);
  }

  async updateStatus(id: number, data: SubscriptionRequestStatusUpdateRq): Promise<SubscriptionRequestHistoryDTO> {
    const result = await API.patch<SubscriptionRequestHistoryDTO>(`/api/v1/subscription-requests/${id}/status`, data);
    return result.data;
  }

  // 구독 요청 처리 내역 추가
  async createHistory(data: SubscriptionRequestHistoryCreateDTO): Promise<SubscriptionRequestHistoryDTO> {
    const formData = new FormData();

    // request 객체를 JSON 문자열로 변환하여 추가
    const requestData = {
      comment: data.comment,
      managerId: data.managerId,
      status: data.status,
      subscriptionRequestId: data.subscriptionRequestId,
    };
    formData.append('request', JSON.stringify(requestData));

    // 파일이 있으면 추가
    if (data.files && data.files.length > 0) {
      data.files.forEach(file => {
        formData.append('files', file);
      });
    }

    const result = await API.post<SubscriptionRequestHistoryDTO>(
      `/api/v1/subscription-requests/${data.subscriptionRequestId}/subscription-request-histories`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return result.data;
  }

  // 구독 요청 처리 내역 수정
  async updateHistory(data: SubscriptionRequestHistoryUpdateDTO): Promise<SubscriptionRequestHistoryDTO> {
    const formData = new FormData();
    const requestData = {
      comment: data.comment,
      subscriptionRequestHistoryId: data.subscriptionRequestHistoryId,
      subscriptionRequestId: data.subscriptionRequestId,
    };
    formData.append('request', JSON.stringify(requestData));

    if (data.files && data.files.length > 0) {
      data.files.forEach(file => {
        formData.append('files', file);
      });
    }

    const result = await API.put<SubscriptionRequestHistoryDTO>(
      `/api/v1/subscription-requests/${data.subscriptionRequestId}/subscription-request-histories/${data.subscriptionRequestHistoryId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return result.data;
  }

  // 구독 요청 처리 내역 삭제
  async deleteHistory(data: SubscriptionRequestHistoryDeleteRq): Promise<void> {
    await API.delete<void>(
      `/api/v1/subscription-requests/${data.subscriptionRequestId}/subscription-request-histories/${data.subscriptionRequestHistoryId}`,
    );
  }
}
